<template>
	<div class="track-selection mt-3">
		<div class="d-flex align-center">
			<strong class="fs--14 fw--400" :class="!isMusicEnabled && 'disabledForm--text'">Track Selection</strong>
			<v-chip color="pink" class="ml-2" small>New</v-chip>
		</div>

		<p class="fieldFore--text fs--12 fw--400 mt-2 mb-2">
			Enabled music tracks will play at random during a Sticker Party. You can disable or enable individual tracks
			to suit your preferences.
		</p>
		<p class="fieldFore--text fs--12 fw--400 mt-2 mb-0">
			All tracks are licensed to be used with this extension and are DMCA cleared for live streaming.
		</p>

		<v-row no-gutters>
			<v-col cols="12" sm="7" md="12" lg="7" xl="7">
				<v-data-table
					class="activity-feed-table"
					:headers="headers"
					:items="stickerPartyTracks"
					:items-per-page="5"
					:hide-default-footer="stickerPartyTracks.length < 1">
					<template v-slot:[`item.enabled`]="{ item }">
						<v-checkbox
							v-model="item.isEnabled"
							:disabled="!isMusicEnabled || isDisabledCustomTrack(item)"
							color="pink"
							class="ma-0"
							hide-details></v-checkbox>
					</template>
					<template v-slot:[`item.actions`]="{ item }">
						<v-btn icon @click="actions.deleteCustomTrack(item)" title="Delete Custom Track">
							<v-icon v-if="item.isCustom" color="pink">
								{{ icons.delete }}
							</v-icon>
						</v-btn>
					</template>
					<template v-slot:[`item.title`]="{ item }">
						<strong
							class="fs--14 fw--400"
							:class="!isMusicEnabled || isDisabledCustomTrack(item) ? 'disabledForm--text' : ''"
							:style="item.isCustom ? 'color: #f1bf47' : ''">
							{{ item.name }}
						</strong>
					</template>
					<template v-slot:[`item.action`]="{ item }">
						<v-btn
							icon
							@click="actions.playTrack(item)"
							:disabled="!isMusicEnabled || isDisabledCustomTrack(item)">
							<v-icon v-if="!playingTrack || (playingTrack && playingTrack.id !== item.id)" color="pink">
								{{ icons.play }}
							</v-icon>

							<v-icon v-if="playingTrack && playingTrack.id === item.id" color="pink">
								{{ icons.pause }}
							</v-icon>
						</v-btn>
					</template>
				</v-data-table>
			</v-col>
			<v-col
				v-if="$vuetify.breakpoint.smAndUp"
				cols="12"
				sm="5"
				md="12"
				lg="5"
				xl="5"
				order="first"
				order-sm="last"
				order-md="first"
				order-lg="last"
				order-xl="last"
				align-self="center"
				class="text-center">
				<template v-if="$vuetify.breakpoint.smOnly">
					<img
						src="@/assets/images/mascot/UpdatedDJ.png"
						height="90%"
						width="90%"
						alt="Mascott DJ"
						class="ml-5" />
				</template>
				<template v-if="$vuetify.breakpoint.mdOnly">
					<img
						src="@/assets/images/mascot/UpdatedDJ.png"
						height="100%"
						class="ml-5"
						width="80%"
						alt="Mascott DJ" />
				</template>
				<template v-if="$vuetify.breakpoint.lgAndUp">
					<img src="@/assets/images/mascot/UpdatedDJ.png" width="125%" class="ml-2" alt="Mascott DJ" />
				</template>
			</v-col>
		</v-row>
		<!-- Custom party music track, requires subscription. -->
		<v-row no-gutters>
			<v-col cols="12">
				<div class="d-flex align-center">
					<strong class="fs--14 fw--400" :class="!isMusicEnabled && 'disabledForm--text'">
						Upload your own music track
					</strong>
					<img
						v-if="!isCustomTrackAllowed"
						src="@/assets/icons/lock_plus.png"
						class="lock-icon cursor-pointer"
						title="Subscribe to unlock"
						@click="actions.gotoSubscriptionsPage" />

					<SubscriberChip :sticker="{ subTier: 1 }" class="ml-2" />
				</div>
				<p class="fieldFore--text fs--12 fw--400 mt-2 mb-2">
					Add your own music tracks to the Track List.
					<a href="https://www.twitch.tv/p/en/legal/dmca-guidelines/" target="_blank">DMCA Notice.</a>
				</p>

				<v-row class="mt-2">
					<v-col cols="7" sm="8" md="8">
						<v-text-field
							v-model="customTrackName"
							background-color="purple"
							placeholder="Enter Track Title"
							:disabled="!config.partyModeEnabled"
							solo
							flat
							dense
							hide-details></v-text-field>
					</v-col>
					<v-col cols="5" sm="4" md="4" class="text-center">
						<v-btn
							v-if="!customTrackData"
							@click="actions.pickCustomTrackFile"
							color="pink"
							:disabled="!config.partyModeEnabled"
							block
							depressed>
							Choose MP3
						</v-btn>
						<div v-else>
							<v-btn
								@click="actions.toggleCustomTrackPreview"
								class="mr-2"
								color="pink"
								title="Preview Audio"
								icon
								depressed>
								<v-icon>{{ !isCustomTrackPlaying ? icons.preview : icons.stopPreview }}</v-icon>
							</v-btn>
							<v-btn
								@click="actions.saveCustomTrack"
								:loading="isCustomTrackSaving"
								color="pink"
								depressed>
								Submit
							</v-btn>
							<v-btn icon @click="actions.clearCustomTrackUpload()" title="Clear">
								<v-icon>
									{{ icons.delete }}
								</v-icon>
							</v-btn>
						</div>
					</v-col>
				</v-row>
				<v-row class="mt-2">
					<v-col cols="7" sm="8" md="8" class="fieldFore--text fs--12 fw--400">
						Max Length 1:30 Seconds 5mb
					</v-col>
					<v-col cols="5" sm="4" md="4" class="text-center">
						<span class="error--text fs--12 fw--400" v-if="customTrackError">{{ customTrackError }}</span>
					</v-col>
				</v-row>
				<div class="mb-2 d-flex justify-space-between align-end">
					<strong class="fs--12 fw--400" :class="!isMusicEnabled && 'disabledForm--text'">
						Play only my music tracks
					</strong>
					<v-switch
						v-model="config.isCustomPartyMusicOnly"
						color="pink"
						:disabled="!config.partyModeEnabled"
						hide-details></v-switch>
				</div>
				<p class="fieldFore--text fs--12 fw--400">
					When enabled, Sticker Party will exclusively play your personal tracks. All other Sticker Party
					tracks, including both existing and future additions, will be automatically disabled.
				</p>
			</v-col>
			<input
				type="file"
				accept="audio/mp3,audio/mav"
				class="d-none"
				@change="actions.onCustomTrackFilePicked"
				ref="customTrackInput" />
		</v-row>
	</div>
</template>

<script>
import { computed, ref, onUnmounted } from '@vue/composition-api';
import store from '@/store';
import { useRouter } from '@core/utils';
import axios from '@axios';
import SubscriberChip from '@/views/streamer/stickers/SubscriberChip.vue';
import { showGlobalError } from '@/utils/error.js';
import { createObjectUrlFromBlob } from '@/utils/sticker.js';
import { mdiDelete, mdiPlayCircle, mdiPauseCircle, mdiStopCircle } from '@mdi/js';

export default {
	props: {
		config: {
			type: Object,
			required: true,
		},
	},
	components: {
		SubscriberChip,
	},
	emits: ['onCustomTrackListChanged'],
	setup(props, ctx) {
		const { router } = useRouter();

		const headers = [
			{
				text: 'Enable',
				align: 'start',
				sortable: true,
				value: 'enabled',
			},
			{ text: '', value: 'actions', sortable: false, filterable: false, align: 'start' },
			{ text: 'Track Title', value: 'title', sortable: true, filterable: true, align: 'start' },
			{ text: 'Preview', value: 'action', sortable: false, filterable: false, align: 'end' },
		];

		const playingTrack = ref(false);
		let audio = null;

		// Custom music track upload.
		let customTrackAudio = null;
		const customTrackInput = ref(null);
		const customTrackFile = ref(null);
		const customTrackName = ref(null);
		const customTrackData = ref(null);
		const customTrackError = ref(null);
		const isCustomTrackSaving = ref(false);
		const isCustomTrackPlaying = ref(false);

		const isMusicEnabled = computed(() => props.config.partyModeEnabled && props.config.partyModeMusic);

		// Calculate track volume.
		const volume = computed(() => {
			const c = props.config;

			let masterVolume = c.masterVolume;
			if (masterVolume === undefined || masterVolume === null) masterVolume = 50;

			let partyMusicVolume = c.partyMusicVolume;
			if (partyMusicVolume === undefined || partyMusicVolume === null) partyMusicVolume = 100;

			const maxRange = 100;
			return (masterVolume / maxRange) * (partyMusicVolume / maxRange);
		});

		// Current subscription plan.
		const currentSubPlan = computed(() =>
			store.state.app.subPlans.find(plan => plan.tier === store.state.app.subTier),
		);

		// Does the current subscription plan allow custom tracks?
		const isCustomTrackAllowed = computed(() => currentSubPlan.value && currentSubPlan.value.customPartyMusic);

		// Is the track a custom track that has been disabled due to subscription downgrade?
		const isDisabledCustomTrack = track => {
			return track.isCustom && !isCustomTrackAllowed.value;
		};

		// Convenience getter for sticker party tracks.
		const stickerPartyTracks = computed(() => {
			return props.config.stickerPartyTracks || [];
		});

		const actions = {
			playTrack(track) {
				// If same Track is already playing then pause
				if (playingTrack.value && playingTrack.value.id === track.id && audio) {
					audio.pause();
					playingTrack.value = null;
					return;
				}

				playingTrack.value = { ...track };
				// If any other track is playing then pause that before playing new track
				if (audio) {
					audio.pause();
				}

				audio = new Audio(track.url);
				audio.volume = volume.value;
				audio.play();

				audio.addEventListener('ended', () => {
					audio = null;
					playingTrack.value = null;
				});
			},
			gotoSubscriptionsPage() {
				router.push('/premium');
			},
			// Browse to pick file.
			pickCustomTrackFile: function () {
				if (isCustomTrackAllowed.value) {
					customTrackInput.value.click();
				} else {
					actions.gotoSubscriptionsPage();
				}
			},
			// File picked.
			onCustomTrackFilePicked: function (e) {
				const validTypes = ['audio/mp3', 'audio/mpeg'];
				const maxFileSize = 5000000; // 5MB
				const file = e.target.files[0];

				if (file) {
					if (file.size > maxFileSize) {
						showGlobalError('Sorry, max recording size is 5MB.');
						return;
					} else if (!validTypes.includes(file.type)) {
						showGlobalError(
							'Sorry! This format is not supported. Audio must be uploaded in either WAV or MP3 file formats.',
						);
						return;
					}

					customTrackFile.value = file;

					const reader = new FileReader();
					reader.readAsDataURL(file);

					reader.onload = function () {
						customTrackData.value = reader.result;
					};
				}
			},
			// Upload custom track.
			saveCustomTrack: function () {
				// Validate.
				customTrackError.value = null;
				if (!customTrackName.value) {
					customTrackError.value = 'Please enter a track name.';
					return;
				}

				const payload = { name: customTrackName.value, data: customTrackData.value };
				isCustomTrackSaving.value = true;

				axios
					.post('/party-music-track', payload)
					.then(() => {
						// Clear form data.
						customTrackFile.value = null;
						customTrackName.value = null;
						customTrackData.value = null;

						// Trigger data reload.
						ctx.emit('onCustomTrackListChanged');
					})
					.catch(error => console.log(error))
					.finally(() => (isCustomTrackSaving.value = false));
			},
			// Delete custom track.
			deleteCustomTrack: function (item) {
				axios
					.delete(`/party-music-track/${item.customId}`)
					.then(() => {
						// Trigger data reload.
						ctx.emit('onCustomTrackListChanged');
					})
					.catch(error => console.log(error));
			},
			// Preview custom track audio before uploading.
			toggleCustomTrackPreview: function () {
				if (customTrackAudio) {
					customTrackAudio.pause();
					customTrackAudio = null;
					isCustomTrackPlaying.value = false;
					return;
				}

				customTrackAudio = new Audio(createObjectUrlFromBlob(customTrackData.value));
				customTrackAudio.volume = volume.value;
				customTrackAudio.play();

				isCustomTrackPlaying.value = true;

				customTrackAudio.addEventListener('ended', () => {
					customTrackAudio = null;
					isCustomTrackPlaying.value = false;
				});
			},
			// Clear the custom track upload form.
			clearCustomTrackUpload: function () {
				customTrackFile.value = null;
				customTrackName.value = null;
				customTrackData.value = null;
				customTrackInput.value.value = null;
			},
		};

		onUnmounted(() => {
			if (audio) {
				audio.pause();
				audio = null;
				playingTrack.value = null;
			}
		});

		return {
			headers,

			customTrackInput,
			customTrackFile,
			customTrackName,
			customTrackData,
			customTrackError,
			isCustomTrackSaving,
			isCustomTrackPlaying,

			isMusicEnabled,
			playingTrack,
			audio,

			currentSubPlan,
			isCustomTrackAllowed,
			isDisabledCustomTrack,
			stickerPartyTracks,

			actions,

			icons: {
				delete: mdiDelete,
				play: mdiPlayCircle,
				pause: mdiPauseCircle,
				preview: mdiPlayCircle,
				stopPreview: mdiStopCircle,
			},
		};
	},
};
</script>
<style lang="scss" scoped>
.lock-icon {
	width: 20px;
	height: 20px;
	margin-left: 10px;
}
</style>
